/**
 * 작성자 : 홍선영
 * 날짜 : 2024.10.28
 * 기능 : 사원관리-사원정보-사원정보/메뉴관리 페이지
 */

import { IModal, ISelectBox } from 'customTypes';
import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { useRecoilValue } from 'recoil';
import { toast } from 'react-toastify';

import { userState } from '../../../atoms';
import { COMCD_LOCK_YN, COMCD_USE_YN, USER_GRANT } from '../../../_constants';
import { checkPhonNumEmpty } from '../../../utils/checkPhonNumEmpty';
import { emailRegex } from '../../../utils/checkRegex';
import { checkUserInputRequiredValue } from '../../../utils/checkUserInputRequiredValue';
import { trimObject } from '../../../utils/trimObject';
import { registerTempPwd } from '../../../services/register';
import { logPost } from '../../../services/log';
import { useTranslation } from 'react-i18next';
import Portal from '../../../components/Portal';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import { apiDelete, apiPatch, apiPost } from '../../../services/_common';
import UserInfoInputFormCompany from '../../../components/Form/UserInfoInputFormCompany';
import { getAnnualnum } from '../../../utils/getAnnualnum';

type UpdatedLogoState = 'N' | 'D' | 'U';

interface Iprops {
  orgUserInfo: any;
  setOrgUserInfo: Dispatch<SetStateAction<any>>;
  applyFilter: (array: any[]) => void;
  orgUserList: any[] | null;
  setOrgUserList: Dispatch<SetStateAction<any | null>>;
  getUserHeadAPI: () => void;
  lockYnComCdList: any;
  useYnComCdList: any;
  userGrantComCdList: any;
  isNewAdd: boolean;
  setIsNewAdd: Dispatch<SetStateAction<boolean>>;
  setSelectedRowKey: Dispatch<SetStateAction<any>>;
  hpNumState: any;
  telNumState: any;
  ipInfoState: any;
  setHpNumState: Dispatch<SetStateAction<any>>;
  setTelNumState: Dispatch<SetStateAction<any>>;
  setIpInfoState: Dispatch<SetStateAction<any>>;
  emerNumState: any;
  setEmerNumState: Dispatch<SetStateAction<any>>;
  useYn: any;
  lockYn: any;
  setUseYn: Dispatch<SetStateAction<any>>;
  setLockYn: Dispatch<SetStateAction<any>>;
  partList: ISelectBox[];
  positionList: ISelectBox[];
  partCd: ISelectBox;
  positionCd: ISelectBox;
  setPartCd: Dispatch<SetStateAction<any>>;
  setPositionCd: Dispatch<SetStateAction<any>>;
  wWorkstatus: ISelectBox;
  setWWorkstatus: Dispatch<SetStateAction<ISelectBox>>;
  gCd: { type: 'USER_GRANT'; [USER_GRANT]: string; cdName: string };
  setGCd: Dispatch<SetStateAction<{ type: 'USER_GRANT'; [USER_GRANT]: string; cdName: string }>>;
  uJobdate: string;
  setUJobdate: Dispatch<SetStateAction<string>>;
  uBdate: string;
  setUBdate: Dispatch<SetStateAction<string>>;
  uRetiredate: string;
  setURetiredate: Dispatch<SetStateAction<string>>;
  userImage: string;
  setUserImage: Dispatch<SetStateAction<string>>;
  addBtnAction: () => void;
  logoType: any | null;
  setLogoType: Dispatch<SetStateAction<any | null>>;
  isUpdatedLogo: UpdatedLogoState;
  setIsUpdatedLogo: Dispatch<SetStateAction<UpdatedLogoState>>;
}
const UserInfo = ({
  orgUserInfo,
  setOrgUserInfo,
  applyFilter,
  orgUserList,
  setOrgUserList,
  getUserHeadAPI,
  lockYnComCdList,
  useYnComCdList,
  userGrantComCdList,
  isNewAdd,
  setIsNewAdd,
  setSelectedRowKey,
  hpNumState,
  telNumState,
  ipInfoState,
  setHpNumState,
  setTelNumState,
  setIpInfoState,
  emerNumState,
  setEmerNumState,
  useYn,
  lockYn,
  setUseYn,
  setLockYn,
  partList,
  positionList,
  partCd,
  positionCd,
  setPartCd,
  setPositionCd,
  wWorkstatus,
  setWWorkstatus,
  gCd,
  setGCd,
  uJobdate,
  setUJobdate,
  uBdate,
  setUBdate,
  uRetiredate,
  setURetiredate,
  userImage,
  setUserImage,
  addBtnAction,
  logoType,
  setLogoType,
  isUpdatedLogo,
  setIsUpdatedLogo,
}: Iprops) => {
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const userIdRef = useRef<HTMLInputElement>(null);
  const userNameRef = useRef<HTMLInputElement>(null);
  const eMailRef = useRef<HTMLInputElement>(null);

  const [rowState, setRowState] = useState({
    userId: '',
    userName: '',
    hpNum: '',
    telNum: '',
    eMail: '',
    ipInfo: '',
    bigo: '',
    eDate: '',
    editor: '',
    lockYn: 'N',
    useYn: 'Y',
    approvalYn: '',
    userPwd: '',
    wDate: '',
    writer: '',
    loginerrorCount: '',
    isDuplicateChecked: false,
    gCd: '',
    partCd: '',
    positionCd: '',
    gName: '',
    uBdate: '',
    uBgubun: '0',
    uWorkstatus: 'I',
  }); // 서버에서 받아 온 raw data 어레이 중 1줄의 데이터 state
  const userInfoInputFormRef = useRef<any>(null); // 인풋 폼 ref (테이블 로우를 클릭했을 때 바로 inputForm 으로 스크롤 이동시키기 위한 ref)
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });

  useEffect(() => {
    if (rowState.gCd) setGCd({ type: USER_GRANT, [USER_GRANT]: rowState.gCd, cdName: rowState.gName });
    else setGCd({ type: USER_GRANT, [USER_GRANT]: '', cdName: t('미선택') });

    setUseYn({ type: COMCD_USE_YN, [COMCD_USE_YN]: rowState.useYn });
    setLockYn({ type: COMCD_LOCK_YN, [COMCD_LOCK_YN]: rowState.lockYn });
  }, [rowState]);

  // TODO 코드정리
  useEffect(() => {
    if (orgUserInfo) {
      const newUserInfo = orgUserList?.find((v) => v.userId === orgUserInfo.userId);
      if (newUserInfo) {
        setIsNewAdd(false);
        // orgUserList는 서버 원본(시간 단위)이므로 화면 표시용 일수로 환산해야 함
        setRowState({
          isDuplicateChecked: true,
          ...newUserInfo,
          uAnnualnum: (Number(newUserInfo.uAnnualnum) / 8).toString(),
          uRannualnum: (Number(newUserInfo.uRannualnum) / 8).toString(),
        });
      }
    }
  }, [orgUserList]);

  useEffect(() => {
    if (orgUserInfo) {
      setIsNewAdd(false);
      setRowState({ isDuplicateChecked: true, ...orgUserInfo });
    }
  }, [orgUserInfo]);

  useEffect(() => {
    if (isNewAdd) initiateState();
  }, [isNewAdd]);

  const onClickNewRegistration = () => {
    setIsNewAdd(true);
    setSelectedRowKey(null);
    initiateState();
  };

  // 스테이트 객체의 밸류를 ''로 초기화
  const initiateState = () => {
    setOrgUserInfo(null);
    Object.keys(rowState).map((el: any) => {
      return setRowState((prev: any) => ({ ...prev, [el]: '', isDuplicateChecked: false, useYn: 'Y', lockYn: 'N', uAnnualnum: '12', uRannualnum: '0', uBgubun: '0', uBdate: '', uWorkstatus: 'I' }));
    });
    setIsSaveClicked(false);
  };

  // 신규등록 API
  const insertNewUserAPI = async (resultData: any) => {
    const trimData = trimObject(resultData);
    const req = {
      userId: trimData.userId,
      userName: trimData.userName,
      gCd: trimData.gCd,
      hCd: trimData.hCd,
      hpNum: trimData.hpNum,
      telNum: trimData.telNum || '',
      eMail: trimData.eMail,
      ipInfo: trimData.ipInfo,
      useYn: trimData.useYn,
      lockYn: trimData.lockYn,
      bigo: trimData.bigo,
      uJobdate: trimData.uJobdate || '',
      uRetiredate: trimData.uRetiredate || '',
      partCd: trimData.partCd,
      positionCd: trimData.positionCd,
      uImg: isUpdatedLogo === 'D' ? '' : trimData.uImg,
      // uImgBase64: isUpdatedLogo === 'U' ? { uImgBase64: userImage.split(',')[1] || '' } : { uImgBase64: '' },
      ...(isUpdatedLogo === 'U' ? { uImgBase64: userImage.split(',')[1] || '' } : { uImgBase64: '' }),
      emerNum: trimData.emerNum || '',
      address1: trimData.address1,
      address2: trimData.address2,
      zipcode: trimData.postNum,
      uAnnualnum: (Number(trimData.uAnnualnum) * 8).toString(),
      uBgubun: trimData.uBgubun,
      uBdate: trimData.uBdate || '',
      uWorkstatus: trimData.uWorkstatus,
    };

    const res = await apiPost({ path: '/user/company', contentType: 'application/json', req });

    const { statusCode, data } = res.data;
    if (statusCode === 200) {
      const findUser = data.find((el: any) => el.userId === resultData.userId);
      if (findUser) {
        setRowState({
          ...findUser,
          postNum: findUser.zipcode,
          isDuplicateChecked: true,
          uAnnualnum: (Number(findUser.uAnnualnum) / 8).toString(),
          uRannualnum: (Number(findUser.uRannualnum) / 8).toString(),
        });
        applyFilter(data);
        setOrgUserList(data);
        setOrgUserInfo({ ...findUser, postNum: findUser.zipcode, uAnnualnum: (Number(findUser.uAnnualnum) / 8).toString(), uRannualnum: (Number(findUser.uRannualnum) / 8).toString() });
        setIsNewAdd(false);
        setIsSaveClicked(false);
        toast.success(`${findUser.userName} ${t('사내 사용자 등록 성공')}`);

        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '사원 정보 / 메뉴 관리',
          action: '신규등록',
          etc: `${resultData.userName}(${resultData.userId})`,
        });
      }
    }
  };

  // 수정 API
  const updateUserAPI = async (resultData: any) => {
    const trimData = trimObject(resultData);
    const req = {
      userId: trimData.userId,
      userName: trimData.userName,
      gCd: trimData.gCd,
      hCd: trimData.hCd,
      hpNum: trimData.hpNum || '',
      telNum: trimData.telNum || '',
      eMail: trimData.eMail,
      ipInfo: trimData.ipInfo,
      useYn: trimData.useYn,
      lockYn: trimData.lockYn,
      bigo: trimData.bigo,
      uJobdate: trimData.uJobdate,
      uRetiredate: trimData.uRetiredate,
      partCd: trimData.partCd,
      positionCd: trimData.positionCd,
      uImg: isUpdatedLogo === 'D' ? '' : trimData.uImg,
      // uImgBase64: isUpdatedLogo === 'U' ? { uImgBase64: userImage.split(',')[1] || '' } : { uImgBase64: '' },
      ...(isUpdatedLogo === 'U' ? { uImgBase64: userImage.split(',')[1] || '' } : { uImgBase64: '' }),
      emerNum: trimData.emerNum || '',
      address1: trimData.address1,
      address2: trimData.address2,
      zipcode: trimData.postNum,
      uAnnualnum: (Number(trimData.uAnnualnum) * 8).toString(),
      uBgubun: trimData.uBgubun,
      uBdate: trimData.uBdate || '',
      uWorkstatus: trimData.uWorkstatus,
    };

    const res = await apiPatch({ path: '/user/company', contentType: 'application/json', req });

    const { statusCode, data } = res.data;
    if (statusCode === 200) {
      const findUser = data.find((el: any) => el.userId === resultData.userId);
      if (findUser) {
        setRowState({
          ...findUser,
          postNum: findUser.zipcode,
          isDuplicateChecked: true,
          uAnnualnum: (Number(findUser.uAnnualnum) / 8).toString(),
          uRannualnum: (Number(findUser.uRannualnum) / 8).toString(),
        });
        applyFilter(data);
        setOrgUserList(data);
        setOrgUserInfo({ ...findUser, postNum: findUser.zipcode, uAnnualnum: (Number(findUser.uAnnualnum) / 8).toString(), uRannualnum: (Number(findUser.uRannualnum) / 8).toString() });
        setIsNewAdd(false);
        setIsSaveClicked(false);
        toast.success(`${findUser.userName} ${t('정보 수정 성공')}`);

        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '사원 정보 / 메뉴 관리',
          action: '수정',
          etc: `${resultData.userName}(${resultData.userId})`,
        });
      }
    }
  };

  // 승인 API
  const updateUserApprovalAPI = async (req: any) => {
    const res = await apiPatch({ path: '/user/approval', req });
    const { statusCode, message, data } = res.data;
    if (statusCode === 200) {
      toast.success(t(message));
      setRowState((prev) => ({ ...prev, approvalYn: 'Y', uAnnualnum: getAnnualnum(data.uAnnualnum), uRannualnum: getAnnualnum(data.uRannualnum) }));
      // orgUserList는 서버 원본(시간 단위)을 유지한다. 환산은 rowState에 담을 때만 한다.
      const newUserList = orgUserList?.map((v: any) =>
        v.userId === req.userId ? { ...v, uWorkstatus: data.uWorkstatus, approvalYn: 'Y', postNum: v.zipcode, uAnnualnum: data.uAnnualnum, uRannualnum: data.uRannualnum } : v
      );
      newUserList && applyFilter(newUserList);
      setOrgUserList(newUserList);
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '사원 정보 / 메뉴 관리',
        action: '승인',
        etc: `${rowState.userName}(${rowState.userId})`,
      });
    }
  };

  // 저장버튼 클릭해서 save API 호출하여 데이터전송
  const onClickSave = async () => {
    setIsSaveClicked(true);
    const mergedHpNumber = `${hpNumState.num1}-${hpNumState.num2}-${hpNumState.num3}`;
    const mergedTelNumber = `${telNumState.num1}-${telNumState.num2}-${telNumState.num3}`;
    const mergedIpNumber =
      ipInfoState.num1 === '' && ipInfoState.num2 === '' && ipInfoState.num3 === '' && ipInfoState.num4 === '' ? '' : `${ipInfoState.num1}.${ipInfoState.num2}.${ipInfoState.num3}.${ipInfoState.num4}`;
    const mergedEmerNumber = `${emerNumState.num1}-${emerNumState.num2}-${emerNumState.num3}`;

    const { eMail } = rowState;
    const { isUserInputRequiredValuePass, userInputRequiredValueMsg, info } = checkUserInputRequiredValue(rowState);
    const { isEmpty, emptyAlertMessage } = checkPhonNumEmpty(hpNumState);
    const { isEmailRegexPass, emailRegexAlertMsg } = emailRegex(eMail);
    if (gCd[USER_GRANT] === '') return toast.warning(t('권한을 선택하세요'));
    if (!isUserInputRequiredValuePass) {
      if (info === 'userId') {
        userIdRef.current?.focus();
      } else if (info === 'userName') {
        userNameRef.current?.focus();
      }
      return toast.warning(t(userInputRequiredValueMsg));
    }
    if (!isEmailRegexPass) {
      eMailRef.current?.focus();
      return toast.warning(t(emailRegexAlertMsg));
    }
    if (isEmpty) return toast.warning(t(emptyAlertMessage));
    if (!partCd.PART_CD) return toast.warning(t('부서명을 선택하세요'));
    if (!positionCd.POSITION_CD) return toast.warning(t('직급명을 선택하세요'));

    // ! sCd: '00000' 고정값
    const resultData = {
      ...rowState,
      hpNum: mergedHpNumber !== '--' ? mergedHpNumber : '',
      telNum: mergedTelNumber !== '--' ? mergedTelNumber : '',
      ipInfo: mergedIpNumber,
      emerNum: mergedEmerNumber !== '--' ? mergedEmerNumber : '',
      gCd: gCd[USER_GRANT],
      sCd: '00000',
      hCd: userInfo.hCd,
    };
    if (isNewAdd) return insertNewUserAPI(resultData);
    return updateUserAPI(resultData);
  };

  // 승인 버튼 클릭
  const onClickApproval = async () => {
    const reqData = {
      userId: rowState.userId,
      editor: userInfo.userId,
      gCd: 'C',
    };
    updateUserApprovalAPI(reqData);
  };

  // 비밀번호 초기화 요청 API
  const initPasswordAPI = async () => {
    const res = await registerTempPwd(rowState.userId, rowState.eMail);
    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      toast.success(t(message));
      getUserHeadAPI();
    }
  };

  // 비밀번호 초기화 버튼 클릭
  const onClickInitPwd = () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'initPw', api: initPasswordAPI }));
  };

  const onClickDelete = async () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: deleteAPI }));
  };

  // 삭제 API
  const deleteAPI = async () => {
    const req = { userId: rowState.userId };
    const res = await apiDelete({ path: '/user/company', req });
    const { message, statusCode, data } = res.data;
    if (statusCode === 200) {
      toast.success(t(message));
      applyFilter(data);
      setOrgUserList(data);
      initiateState();
      setIsNewAdd(true);
      setSelectedRowKey(null);
      addBtnAction();
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '사원 정보 / 메뉴 관리',
        action: '삭제',
        etc: `${rowState.userName}(${rowState.userId})`,
      });
    } else {
      // toast.error(t(ERROR));
    }
  };

  return (
    <>
      <UserInfoInputFormCompany
        style={{ width: '100%' }}
        state={rowState}
        setState={setRowState}
        isNewAdd={isNewAdd}
        userInfoInputFormRef={userInfoInputFormRef}
        onClickNewAdd={onClickNewRegistration}
        onClickSave={onClickSave}
        onClickApproval={onClickApproval}
        onClickDelete={onClickDelete}
        onClickInitPwd={onClickInitPwd}
        telNumState={telNumState}
        setTelNumState={setTelNumState}
        hpNumState={hpNumState}
        setHpNumState={setHpNumState}
        ipInfoState={ipInfoState}
        setIpInfoState={setIpInfoState}
        emerNumState={emerNumState}
        setEmerNumState={setEmerNumState}
        gCd={gCd}
        setGCd={setGCd}
        lockYn={lockYn}
        useYn={useYn}
        setUseYn={setUseYn}
        setLockYn={setLockYn}
        lockYnComCdList={lockYnComCdList}
        useYnComCdList={useYnComCdList}
        userGrantComCdList={userGrantComCdList}
        userIdRef={userIdRef}
        userNameRef={userNameRef}
        eMailRef={eMailRef}
        isSaveClicked={isSaveClicked}
        partList={partList}
        positionList={positionList}
        partCd={partCd}
        setPartCd={setPartCd}
        positionCd={positionCd}
        setPositionCd={setPositionCd}
        wWorkstatus={wWorkstatus}
        setWWorkstatus={setWWorkstatus}
        uJobdate={uJobdate}
        setUJobdate={setUJobdate}
        uBdate={uBdate}
        setUBdate={setUBdate}
        uRetiredate={uRetiredate}
        setURetiredate={setURetiredate}
        userImage={userImage}
        setUserImage={setUserImage}
        setLogoType={setLogoType}
        setIsUpdatedLogo={setIsUpdatedLogo}
      />
      <Portal openModal={openModal?.status}>{openModal && (openModal.type === 'delete' || openModal.type === 'initPw') && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </>
  );
};

export default UserInfo;
